import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { exportGuideToPDF } from '@/lib/pdfExport';
import { 
  ArrowLeft, Download, Building2, CalendarDays, Bell, 
  MessageSquare, BarChart3, CheckCircle, XCircle, Clock,
  Smartphone, Volume2, Shield, UserPlus, Loader2
} from 'lucide-react';
import BrandLogo from '../../components/BrandLogo';

export default function SubGuide() {
  const [isExporting, setIsExporting] = useState(false);

  const handleDownloadPDF = async () => {
    setIsExporting(true);
    try {
      const success = await exportGuideToPDF('sub-guide-content', 'ExpressSub-Sub-User-Guide', 'Substitute Teacher Guide');
      if (success) {
        toast.success('PDF downloaded successfully!');
      } else {
        toast.error('Failed to generate PDF');
      }
    } catch (error) {
      toast.error('Failed to generate PDF');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header - Hidden in print */}
      <header className="bg-white border-b border-slate-200 print:hidden">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/toc">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
            </Link>
            <h1 className="text-xl font-bold text-slate-900">Substitute Teacher Guide</h1>
          </div>
          <Button onClick={handleDownloadPDF} disabled={isExporting} data-testid="download-sub-guide-pdf">
            {isExporting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </>
            )}
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8" id="sub-guide-content">
        {/* PDF Header - visible in export */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <BrandLogo variant="black" size="xl" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Substitute Teacher User Guide</h1>
          <p className="text-slate-600">Your complete guide to finding and accepting positions</p>
        </div>

        {/* Introduction */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-emerald-600" />
              Welcome to ExpressSub
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-600 mb-4">
              ExpressSub connects you with schools looking for substitute teachers. 
              Receive instant notifications, respond quickly, and manage your schedule all in one place.
            </p>
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary">Instant Alerts</Badge>
              <Badge variant="secondary">Easy Responses</Badge>
              <Badge variant="secondary">Multiple Schools</Badge>
              <Badge variant="secondary">Direct Messaging</Badge>
            </div>
          </CardContent>
        </Card>

        {/* Feature Sections */}
        <div className="space-y-6">
          {/* Connecting to Schools */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Building2 className="h-5 w-5 text-blue-600" />
                1. Connecting to Schools
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-slate-600">Join schools to start receiving position offers.</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Get a School Code:</strong> Ask the school administrator for their unique ExpressSub code.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Request to Join:</strong> Click "Connect to School" and enter the code. Your request will be sent to the administrator.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Pending Status:</strong> You'll see "Pending" until the administrator approves your request.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Multiple Schools:</strong> Connect to as many schools as you like to maximize opportunities.</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Viewing Positions */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <CalendarDays className="h-5 w-5 text-orange-600" />
                2. Viewing Available Positions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-slate-600">Browse and filter position offers from your connected schools.</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Position Cards:</strong> Each card shows the school, date(s), time, and category.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Multi-Day Positions:</strong> Some positions span multiple dates. You must accept all dates or decline.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Hide Filled:</strong> Toggle "Hide filled" to only see available positions.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Status Badges:</strong> Open (available), Filled (taken by someone), Accepted (your job).</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Responding to Positions */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
                3. Responding to Positions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-slate-600">Accept or decline positions quickly.</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Accept:</strong> Click the green "Accept" button to take the position. Act fast - first to accept gets it!</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Decline:</strong> Click "Decline" if you can't take the position. You can change to accept later if it's still open.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Double-Booking Protection:</strong> The system prevents you from accepting overlapping positions.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Your Jobs:</strong> Accepted positions show "Your Job" badge with a green border.</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Availability */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <XCircle className="h-5 w-5 text-rose-600" />
                4. Managing Your Availability
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-slate-600">Control when you receive position offers.</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Decline All for Today:</strong> Click this button if you're unavailable for the rest of the day.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Schools Notified:</strong> All your connected schools will be notified that you're unavailable.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>No New Offers:</strong> You won't receive new position notifications while unavailable.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Become Available:</strong> Click "Unavailable Today" again to undo and start receiving offers.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Auto-Reset:</strong> Your availability automatically resets each day.</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Notifications */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Bell className="h-5 w-5 text-amber-600" />
                5. Notifications & Alerts
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-slate-600">Never miss an opportunity with instant alerts.</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Browser Notifications:</strong> Enable notifications to get alerts even when the app isn't open.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Audio Alerts:</strong> Toggle sound on/off using the speaker icon.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Reminders:</strong> Schools can send reminders for positions you haven't responded to.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Bell Icon:</strong> Click the bell to see all your recent notifications.</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Messaging */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <MessageSquare className="h-5 w-5 text-green-600" />
                6. Messaging
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-slate-600">Communicate directly with school administrators.</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Message Schools:</strong> Click the message icon on any connected school to send a message.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Receive Messages:</strong> Schools can message you about positions or other matters.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Unread Count:</strong> See your unread message count in the header.</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Analytics */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <BarChart3 className="h-5 w-5 text-indigo-600" />
                7. Your Statistics
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-slate-600">Track your performance across schools.</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Positions Offered:</strong> See how many positions each school has offered you.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Your Responses:</strong> Track your accepted and declined counts per school.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Jobs Filled:</strong> See how many positions you've successfully filled.</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Tips */}
          <Card className="mb-6 bg-emerald-50 border-emerald-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg text-emerald-800">
                <Smartphone className="h-5 w-5" />
                Pro Tips for Success
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <ul className="space-y-2 text-sm text-emerald-900">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Enable Notifications:</strong> Turn on browser notifications and sounds to respond fastest.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Check Regularly:</strong> Positions fill quickly - check the app frequently during school hours.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Connect to Multiple Schools:</strong> More connections = more opportunities.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Respond Quickly:</strong> Even declining helps schools find coverage faster.</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-slate-200 text-center text-sm text-slate-500">
          <p>© 2026 ExpressSub. All rights reserved.</p>
          <p className="mt-1">Need help? Contact support@expresssub.com</p>
        </div>
      </main>
    </div>
  );
}
